import React, { useState } from "react";
import {
  useGetA2pCampaignsQuery,
  useCreateA2pCampaignMutation,
  useUpdateA2pCampaignMutation,
  useSubmitA2pCampaignByIdMutation,
  useCancelA2pCampaignByIdMutation,
  useAssignA2pCampaignNumbersMutation,
  useGetOwnPhoneNumbersQuery,
} from "@/store/rest_api";
import { A2pCampaign } from "@/model/interface/A2pRegistration";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { USE_CASES } from "./a2pConstants";

const STATUS_BADGE_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  DRAFT: "outline",
  PENDING: "secondary",
  VERIFIED: "default",
  FAILED: "destructive",
  CANCELLED: "secondary",
};

const EMPTY_DRAFT: Partial<A2pCampaign> = {
  useCase: "",
  campaignDescription: "",
  messageFlow: "",
  messageSample1: "",
  messageSample2: "",
  optInMessage: "",
  optOutMessage: "",
  helpMessage: "",
};

export default function A2pCampaignsSection() {
  const { data: campaigns } = useGetA2pCampaignsQuery();
  const { data: allNumbers } = useGetOwnPhoneNumbersQuery();
  const [createCampaign, { isLoading: isCreating }] = useCreateA2pCampaignMutation();
  const [updateCampaign, { isLoading: isUpdating }] = useUpdateA2pCampaignMutation();
  const [submitCampaign, { isLoading: isSubmitting }] = useSubmitA2pCampaignByIdMutation();
  const [cancelCampaign, { isLoading: isCancelling }] = useCancelA2pCampaignByIdMutation();
  const [assignNumbers, { isLoading: isAssigning }] = useAssignA2pCampaignNumbersMutation();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<A2pCampaign>>(EMPTY_DRAFT);
  const [numbersTarget, setNumbersTarget] = useState<A2pCampaign | null>(null);
  const [selectedNumberIds, setSelectedNumberIds] = useState<string[]>([]);
  const [cancelTarget, setCancelTarget] = useState<A2pCampaign | null>(null);

  const updateDraft = (changes: Partial<A2pCampaign>) => {
    setDraft((current) => ({ ...current, ...changes }));
  };

  const openCreate = () => {
    setEditingCampaignId(null);
    setDraft(EMPTY_DRAFT);
    setEditorOpen(true);
  };

  const openEdit = (campaign: A2pCampaign) => {
    setEditingCampaignId(campaign.id ?? null);
    setDraft({ ...campaign });
    setEditorOpen(true);
  };

  const openNumbers = (campaign: A2pCampaign) => {
    setNumbersTarget(campaign);
    setSelectedNumberIds(
      (allNumbers ?? [])
        .filter((phoneNumber) => phoneNumber.a2pCampaign?.id === campaign.id)
        .map((phoneNumber) => phoneNumber.id),
    );
  };

  const handleSaveDraft = async () => {
    try {
      if (editingCampaignId) {
        await updateCampaign({ campaignId: editingCampaignId, data: draft }).unwrap();
        toast.success("Campaign updated");
      } else {
        await createCampaign(draft).unwrap();
        toast.success("Campaign created");
      }
      setEditorOpen(false);
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to save the campaign");
    }
  };

  const handleSubmit = async (campaign: A2pCampaign) => {
    try {
      await submitCampaign(campaign.id!).unwrap();
      toast.success("Campaign submitted for carrier review");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to submit the campaign");
    }
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    try {
      await cancelCampaign(cancelTarget.id!).unwrap();
      toast.success("Campaign cancelled and its numbers un-enrolled");
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to cancel the campaign");
    } finally {
      setCancelTarget(null);
    }
  };

  const handleAssignNumbers = async () => {
    if (!numbersTarget) return;
    try {
      await assignNumbers({ campaignId: numbersTarget.id!, phoneNumberIds: selectedNumberIds }).unwrap();
      toast.success("Numbers assigned to the campaign");
      setNumbersTarget(null);
    } catch (error: any) {
      toast.error(error?.data?.detail || "Failed to assign numbers");
    }
  };

  const toggleNumber = (phoneNumberId: string, checked: boolean) => {
    setSelectedNumberIds((current) =>
      checked ? [...current, phoneNumberId] : current.filter((id) => id !== phoneNumberId),
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Campaigns</CardTitle>
          <CardDescription>
            Each campaign covers one texting use case with its own numbers and monthly fee. Most
            businesses only need the one created with your registration.
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4 mr-1" />
          New Campaign
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {!campaigns?.length && (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No campaigns yet. Your first campaign is created automatically when your registration
            completes.
          </p>
        )}
        {campaigns?.map((campaign) => {
          const numberCount = (allNumbers ?? []).filter(
            (phoneNumber) => phoneNumber.a2pCampaign?.id === campaign.id,
          ).length;
          return (
            <div key={campaign.id} className="rounded-lg border p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">
                    {USE_CASES.find((useCase) => useCase.value === campaign.useCase)?.label ||
                      campaign.useCase ||
                      "Untitled campaign"}
                  </span>
                  <Badge variant={STATUS_BADGE_VARIANTS[campaign.status] || "secondary"}>
                    {campaign.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {numberCount} {numberCount === 1 ? "number" : "numbers"}
                  </span>
                </div>
                <div className="flex gap-2">
                  {(campaign.status === "DRAFT" || campaign.status === "FAILED") && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(campaign)}>
                        Edit
                      </Button>
                      <Button size="sm" disabled={isSubmitting} onClick={() => handleSubmit(campaign)}>
                        {isSubmitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                        Submit
                      </Button>
                    </>
                  )}
                  {(campaign.status === "PENDING" || campaign.status === "VERIFIED") && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openNumbers(campaign)}>
                        Numbers
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => setCancelTarget(campaign)}>
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {campaign.campaignDescription && (
                <p className="text-xs text-muted-foreground line-clamp-2">{campaign.campaignDescription}</p>
              )}
              {campaign.rejectionReason && campaign.status === "FAILED" && (
                <p className="text-xs text-destructive">{campaign.rejectionReason}</p>
              )}
            </div>
          );
        })}
      </CardContent>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaignId ? "Edit Campaign" : "New Campaign"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Use Case</Label>
              <Select value={draft.useCase || ""} onValueChange={(value) => updateDraft({ useCase: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select use case" />
                </SelectTrigger>
                <SelectContent>
                  {USE_CASES.map((useCase) => (
                    <SelectItem key={useCase.value} value={useCase.value}>
                      {useCase.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Campaign Description</Label>
              <Textarea
                rows={3}
                value={draft.campaignDescription || ""}
                onChange={(event) => updateDraft({ campaignDescription: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>How Contacts Opt In</Label>
              <Textarea
                rows={3}
                value={draft.messageFlow || ""}
                onChange={(event) => updateDraft({ messageFlow: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Message Sample 1</Label>
              <Textarea
                rows={2}
                value={draft.messageSample1 || ""}
                onChange={(event) => updateDraft({ messageSample1: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Message Sample 2</Label>
              <Textarea
                rows={2}
                value={draft.messageSample2 || ""}
                onChange={(event) => updateDraft({ messageSample2: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Opt-In Confirmation Message</Label>
              <Textarea
                rows={2}
                value={draft.optInMessage || ""}
                onChange={(event) => updateDraft({ optInMessage: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>Opt-Out (STOP) Reply</Label>
              <Textarea
                rows={2}
                value={draft.optOutMessage || ""}
                onChange={(event) => updateDraft({ optOutMessage: event.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>HELP Reply</Label>
              <Textarea
                rows={2}
                value={draft.helpMessage || ""}
                onChange={(event) => updateDraft({ helpMessage: event.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isCreating || isUpdating} onClick={handleSaveDraft}>
              {(isCreating || isUpdating) && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={numbersTarget !== null} onOpenChange={(open) => !open && setNumbersTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Campaign Numbers</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            A number can belong to one campaign at a time. Assigning a number here moves it from
            its current campaign.
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(allNumbers ?? [])
              .filter((phoneNumber) => phoneNumber.active && phoneNumber.type !== "TOLL_FREE")
              .map((phoneNumber) => (
                <label key={phoneNumber.id} className="flex items-center gap-3 rounded-lg border p-3 text-sm">
                  <Checkbox
                    checked={selectedNumberIds.includes(phoneNumber.id)}
                    onCheckedChange={(checked) => toggleNumber(phoneNumber.id, checked === true)}
                  />
                  <span className="flex-1">
                    {phoneNumber.alias ? `${phoneNumber.alias} (${phoneNumber.number})` : phoneNumber.number}
                  </span>
                  {phoneNumber.a2pCampaign?.id && phoneNumber.a2pCampaign.id !== numbersTarget?.id && (
                    <span className="text-xs text-muted-foreground">on another campaign</span>
                  )}
                </label>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNumbersTarget(null)}>
              Cancel
            </Button>
            <Button disabled={isAssigning} onClick={handleAssignNumbers}>
              {isAssigning && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Assignment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={cancelTarget !== null} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              Its numbers stop sending texts until they are assigned to another campaign, and its
              monthly fee stops. Other campaigns are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Campaign</AlertDialogCancel>
            <AlertDialogAction disabled={isCancelling} onClick={handleCancel}>
              Cancel Campaign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
